export interface Module {
  id: number;
  title: string;
  duration: string;
  type: string;
  description: string;
  whatYoullLearn: string[];
  takeaway: string;
  access: 'PREVIEW AVAILABLE' | 'FULL ACCESS';
  preview: boolean;
  image: string;
  progress?: number;
  completed?: boolean;
}

export const modules: Module[] = [
  {
    id: 1,
    title: "Module 1 Introduction to Principal IT Talent Challenges",
    duration: "20 min",
    type: "text + downloadable report",
    description: "Understand the Czech and US IT markets and why principal talent is critical for success. Learn the challenges and opportunities in hiring senior IT professionals, grounded in real-world examples from our 20+ years of experience.",
    whatYoullLearn: [
      "Market context and the importance of senior talent in CZ (63% shortage) and US (74% shortage).",
      "Key challenges: CZ's undertrained TA teams and US's Big Tech competition.",
      "Real-world examples and practical insights from tech and fintech companies."
    ],
    takeaway: "Czech and US IT Talent Market 2025 Report",
    access: "PREVIEW AVAILABLE",
    preview: true,
    image: "https://images.unsplash.com/photo-1562564055-71e051d33c19?auto=format&fit=crop&q=80&w=1000"
  },
  {
    id: 2,
    title: "Module 2 Defining and Understanding Principal Talent",
    duration: "30 min",
    type: "interactive + worksheet",
    description: "Clarify what makes a principal IT candidate and their unique needs in the Czech and US markets. Build precise job descriptions to attract top talent, using insights from our successes at tech companies.",
    whatYoullLearn: [
      "Characteristics: 10+ years experience, technical mastery (e.g., Python, Kubernetes), and leadership.",
      "Senior motivators: Impactful projects, autonomy, and hybrid options (70% CZ, 30% US).",
      "In-demand roles: AI/ML, cybersecurity, devops in CZ and US."
    ],
    takeaway: "Principal IT Role Job Description Template",
    access: "FULL ACCESS",
    preview: false,
    image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=80&w=1000"
  },
  {
    id: 3,
    title: "Module 3 Advanced Sourcing Strategies",
    duration: "45 min",
    type: "hands-on + templates",
    description: "Master proactive sourcing to find passive senior candidates in CZ and US markets. Learn advanced Boolean searches, GitHub outreach, and community engagement, drawing on experience sourcing for cybersecurity firms, fintech companies, and high-tech startups.",
    whatYoullLearn: [
      "LinkedIn Boolean searches (e.g., \"senior AND AI AND Prague -junior\").",
      "GitHub strategies to identify contributors (e.g., TensorFlow for AI roles).",
      "Engage CZ (Prague Devs) and US (TechCrunch Disrupt) tech communities."
    ],
    takeaway: "Sourcing Cheat Sheet for Principal IT Talent",
    access: "FULL ACCESS",
    preview: false,
    image: "https://images.unsplash.com/photo-1555952517-2e8e729e0b44?auto=format&fit=crop&q=80&w=1000"
  },
  {
    id: 4,
    title: "Module 4 Employer Branding for Senior Talent",
    duration: "45 min",
    type: "interactive + checklist",
    description: "Enhance your company's appeal to attract principal IT candidates in CZ and US markets. Optimise career pages and content to highlight what seniors value, inspired by my work with a Brno SaaS firm.",
    whatYoullLearn: [
      "Showcase tech stacks (e.g., React, AWS) and project impact.",
      "Optimise career pages to attract senior talent (job boards like Jobs.cz don't effectively reach principals).",
      "Publish tech blogs and speak at Prague Tech Fest or TechCrunch Disrupt.",
      "Build trust with senior engineer testimonials."
    ],
    takeaway: "Senior-Friendly Employer Branding Checklist",
    access: "FULL ACCESS",
    preview: false,
    image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=1000"
  },
  {
    id: 5,
    title: "Module 5 Screening and Engaging Senior Candidates",
    duration: "60 min",
    type: "hands-on + templates",
    description: "Improve screening and engagement to secure principal IT hires in CZ and US markets. Use tailored assessments and personalised outreach, leveraging our fintech hire success.",
    whatYoullLearn: [
      "Screen with Codility (CZ) or HackerRank (US) and GitHub portfolios.",
      "Personalise outreach (e.g., \"Your TensorFlow project fits our AI role\").",
      "Avoid pitfalls like over-relying on ATS filters."
    ],
    takeaway: "Outreach and Screening Toolkit for Principal IT Talent",
    access: "FULL ACCESS",
    preview: false,
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=1000"
  },
  {
    id: 6,
    title: "Module 6 Building a Talent Pipeline",
    duration: "45 min",
    type: "interactive + template",
    description: "Create a proactive pipeline to reduce time-to-hire for senior IT candidates in CZ and US markets. Learn to maintain and nurture candidate relationships, based on our Swiss crypto startup pipeline success.",
    whatYoullLearn: [
      "Build a 20–50 candidate database using Airtable/Notion.",
      "Nurture with monthly check-ins (e.g., CzechCrunch for CZ, TechCrunch for US).",
      "Target Prague Devs (CZ) and IEEE (US) for sourcing."
    ],
    takeaway: "Senior IT Talent Pipeline Template",
    access: "FULL ACCESS",
    preview: false,
    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&q=80&w=1000"
  },
  {
    id: 7,
    title: "Module 7 Measuring and Optimising Recruitment Success",
    duration: "30 min",
    type: "self-paced + interactive",
    description: "Guessing what works = slow hires, wasted budget. Track basics (time-to-hire <60 days, acceptance >80%, quality 90%) with simple tools (Google Sheets/ATS) to optimise channels and cut costs. This module teaches metrics framework for CZ/US: select/track/analyse/iterate for 30–40% faster, better hires.",
    whatYoullLearn: [
      "Key metrics (applicants/shortlisted/time-to-hire/acceptance/quality)",
      "Tools (LinkedIn Talent Insights/Google Analytics/Workable/Lever)",
      "Analyse data to shift channels (e.g., low quality → GitHub)",
      "CZ/US tailoring (beat 90-day CZ average, equity for US acceptance)"
    ],
    takeaway: "Metrics Hacker Kit (Dashboard Template Basics)",
    access: "FULL ACCESS",
    preview: false,
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1000"
  },
  {
    id: 8,
    title: "Module 8 Q&A and Action Planning",
    duration: "30 min",
    type: "self-paced + interactive",
    description: "You've mastered sourcing to metrics—now make it real. This final module tackles your company-specific challenges (e.g., competing with giants, low responses) and commits to a 30-day action plan. Turn knowledge into hires: clear actions, owners, deadlines, outcomes for CZ/US markets.",
    whatYoullLearn: [
      "Address real hurdles (ATS filters, Big Tech competition)",
      "Build a 30-day plan (3 actions for <60-day hires)",
      "Set measurable goals/outcomes",
      "CZ/US tailoring (hybrid/Prague Devs CZ, equity/IEEE US)",
      "Track/review for iteration"
    ],
    takeaway: "Action Plan Hacker Kit (30-Day Template Basics)",
    access: "FULL ACCESS",
    preview: false,
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=1000"
  }
];
